import { useMutation } from '@apollo/client';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
  AnnotationItemFragment,
  EventItemFragment,
} from '../../../common-client-queries.generated';
import {
  type AccountingSystemReference,
  BookkeepingData,
  type FieldState,
  type ReceiptState,
} from '../../../components/bookkeeping';
import {
  RemoveReceiptAnnotationDocument,
  SetEventCategoryDocument,
  SetNoteDocument,
  SetParticipantsDocument,
  SetPurposeDocument,
  SetReceiptLostDocument,
  SetReceiptNotNeededDocument,
} from '../../../components/bookkeeping/queries.generated';

export const Bookkeeping: React.FC<{
  accountingSystem?: AccountingSystemReference;
  event: EventItemFragment;
  categorySelectorLabel?: string;
  defaultCategorySelectorText: string;
  categorySuggestionFids?: readonly string[];
  hideCompleteLabel?: boolean;
  /**
   * If a payment from external integration, we dont want to show fields
   * not relevant for the user like category requirements.
   */
  hideRequirements?: boolean;
  showDefaultControlsAsRequired?: boolean;
  editable: boolean;
}> = ({
  accountingSystem,
  event,
  categorySelectorLabel,
  defaultCategorySelectorText,
  categorySuggestionFids = [],
  hideCompleteLabel = false,
  hideRequirements = false,
  showDefaultControlsAsRequired,
  editable,
}) => {
  const {
    fid: eventFid,
    purpose,
    participants,
    receiptNotNeeded,
    receiptLost,
    receipts,
    requirements,
    note,
    ledgerCategoryInfo,
  } = event;
  const { category } = ledgerCategoryInfo;

  const [setCategory, setCategoryResult] = useMutation(
    SetEventCategoryDocument,
  );
  const [setPurpose, setPurposeResult] = useMutation(SetPurposeDocument);
  const [setParticipants, setParticipantsResult] = useMutation(
    SetParticipantsDocument,
  );
  const [setReceiptNotNeeded, setReceiptNotNeededResult] = useMutation(
    SetReceiptNotNeededDocument,
  );
  // Use the same mutation with a different name here since we need separate
  // loading flags for with and without text
  const [setReceiptNotNeededWithText, setReceiptNotNeededWithTextResult] =
    useMutation(SetReceiptNotNeededDocument);
  const [setReceiptLost, setReceiptLostResult] = useMutation(
    SetReceiptLostDocument,
  );
  const [removeReceiptAnnotation, removeReceiptAnnotationResult] = useMutation(
    RemoveReceiptAnnotationDocument,
  );
  const [setNote, setNoteResult] = useMutation(SetNoteDocument);

  const handleSetReceiptState = React.useCallback(
    async (state: ReceiptState, value: string | undefined) => {
      switch (state) {
        case 'default': {
          const annotationFid = receiptNotNeeded?.fid || receiptLost?.fid;
          if (annotationFid) {
            await removeReceiptAnnotation({
              variables: { eventFid, annotationFid },
              optimisticResponse: {
                __typename: 'Mutation',
                removeReceiptAnnotation: {
                  ...event,
                  receiptNotNeeded: null,
                  receiptLost: null,
                },
              },
            });
          }
          break;
        }

        case 'notNeeded': {
          if (value) {
            await setReceiptNotNeededWithText({
              variables: { eventFid, text: value },
              optimisticResponse: {
                __typename: 'Mutation',
                setReceiptNotNeeded: {
                  ...event,
                  receiptNotNeeded: annotation(value),
                  receiptLost: null,
                },
              },
            });
          } else {
            await setReceiptNotNeeded({
              variables: { eventFid, text: '' },
              optimisticResponse: {
                __typename: 'Mutation',
                setReceiptNotNeeded: {
                  ...event,
                  receiptNotNeeded: annotation(''),
                  receiptLost: null,
                },
              },
            });
          }
          break;
        }

        case 'lost': {
          await setReceiptLost({
            variables: { eventFid, text: '' },
            optimisticResponse: {
              __typename: 'Mutation',
              setReceiptLost: {
                ...event,
                receiptNotNeeded: null,
                receiptLost: annotation(''),
              },
            },
          });
          break;
        }
      }
    },
    [
      event,
      eventFid,
      receiptNotNeeded?.fid,
      receiptLost?.fid,
      setReceiptNotNeeded,
      setReceiptNotNeededWithText,
      setReceiptLost,
      removeReceiptAnnotation,
    ],
  );

  return (
    <BookkeepingData
      accountingSystem={accountingSystem}
      eventFid={eventFid}
      categorySelectorLabel={categorySelectorLabel}
      showDefaultControlsAsRequired={
        showDefaultControlsAsRequired ?? category === null
      }
      hideRequirements={hideRequirements}
      showReceiptOptions={true}
      category={category}
      categorySuggestionFids={categorySuggestionFids}
      onSetCategory={category => {
        setCategory({
          variables: {
            info: {
              eventFid,
              kind: 'Selected',
              ledgerFid: category.fid,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            setLedgerCategoryItem: {
              ...event,
              ledgerCategoryInfo: {
                ...ledgerCategoryInfo,
                category: {
                  subtitle: null,
                  synonyms: [],
                  __typename: 'LedgerCategoryItem',
                  ...category,
                },
              },
            },
          },
        });
      }}
      categoryState={getFieldStatus(
        category,
        setCategoryResult.error != null,
        setCategoryResult.loading,
      )}
      defaultCategorySelectorText={defaultCategorySelectorText}
      purpose={purpose?.text}
      onSetPurpose={(text = '') => {
        setPurpose({
          variables: { eventFid, text },
          optimisticResponse: {
            __typename: 'Mutation',
            setPurpose: { ...event, purpose: annotation(text) },
          },
        });
      }}
      purposeState={getFieldStatus(
        emptyAsUndefined(purpose?.text),
        setPurposeResult.error != null,
        setPurposeResult.loading,
      )}
      participants={participants?.text}
      onSetParticipants={(text = '') => {
        setParticipants({
          variables: { eventFid, text },
          optimisticResponse: {
            __typename: 'Mutation',
            setParticipants: { ...event, participants: annotation(text) },
          },
        });
      }}
      participantsState={getFieldStatus(
        emptyAsUndefined(participants?.text),
        setParticipantsResult.error != null,
        setParticipantsResult.loading,
      )}
      note={note?.text}
      onSetNote={(text = '') => {
        setNote({
          variables: { eventFid, text },
          optimisticResponse: {
            __typename: 'Mutation',
            setNote: { ...event, note: annotation(text) },
          },
        });
      }}
      noteState={getFieldStatus(
        emptyAsUndefined(note?.text),
        setNoteResult.error != null,
        setNoteResult.loading,
      )}
      receipts={receipts}
      onSetReceiptFids={() => undefined}
      receiptState={getReceiptState(
        receiptNotNeeded?.fid != null,
        receiptLost?.fid != null,
      )}
      onSetReceiptState={handleSetReceiptState}
      receiptStateError={
        setReceiptNotNeededResult.error != null ||
        setReceiptLostResult.error != null ||
        removeReceiptAnnotationResult.error != null
      }
      receiptIsNotNeededText={receiptNotNeeded?.text}
      receiptIsNotNeededState={getFieldStatus(
        emptyAsUndefined(receiptNotNeeded?.text),
        setReceiptNotNeededWithTextResult.error != null,
        setReceiptNotNeededWithTextResult.loading,
      )}
      allRequirementsMet={requirements.allRequirementsMet}
      hideCompleteLabel={hideCompleteLabel}
      editable={editable}
      requirementsOverridden={requirements.requirementsOverridden.overridden}
    />
  );
};

function emptyAsUndefined(text: string | undefined) {
  return text?.trim() === '' ? undefined : text;
}

function getFieldStatus(
  value: unknown,
  error: boolean,
  loading: boolean,
): FieldState {
  if (error) {
    return 'error';
  }

  if (loading) {
    return 'inProgress';
  }

  if (value == null) {
    return 'notSet';
  }

  return 'set';
}

function getReceiptState(
  receiptNotNeeded: boolean,
  receiptLost: boolean,
): ReceiptState {
  if (receiptNotNeeded) {
    return 'notNeeded';
  }

  if (receiptLost) {
    return 'lost';
  }

  return 'default';
}

function annotation(text: string): AnnotationItemFragment {
  return {
    fid: uuidv4(),
    dateTime: new Date().toISOString(),
    text,
    agentInfo: {
      __typename: 'Agent',
      fid: '',
      structuredName: {
        first: '',
        full: '',
        __typename: 'Name',
      },
    },
    __typename: 'Annotation',
  };
}
